import { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
  Upload,
  FileSpreadsheet,
  ArrowRight,
  ArrowLeft,
  Check,
  AlertCircle,
  Download,
  Loader2,
  Package,
  Users,
  Building2,
  MapPin,
  FolderTree,
  Truck,
  Tag,
  CheckCircle,
  XCircle,
  RefreshCw,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import api from '@/services/api';

interface ImportTemplate {
  id: string;
  name: string;
  description: string;
  requiredFields: string[];
  optionalFields: string[];
}

interface ColumnMapping {
  sourceColumn: string;
  targetField: string;
}

interface ValidationError {
  row: number;
  field: string;
  value: any;
  message: string;
}

interface ImportResult {
  success: boolean;
  entityType: string;
  totalRows: number;
  imported: number;
  skipped: number;
  errors: ValidationError[];
  createdIds: string[];
}

const ENTITY_ICONS: Record<string, typeof Package> = {
  assets: Package,
  employees: Users,
  users: Users,
  branches: Building2,
  departments: Building2,
  locations: MapPin,
  categories: FolderTree,
  vendors: Truck,
  brands: Tag,
};

const STEPS = [
  { id: 1, name: 'Select Type', description: 'Choose what to import' },
  { id: 2, name: 'Upload File', description: 'Upload CSV or Excel' },
  { id: 3, name: 'Map Columns', description: 'Match columns to fields' },
  { id: 4, name: 'Review', description: 'Preview and validate' },
  { id: 5, name: 'Import', description: 'Execute import' },
];

export default function ImportWizardPage() {
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedType, setSelectedType] = useState<string>('');
  const [file, setFile] = useState<File | null>(null);
  const [previewData, setPreviewData] = useState<any>(null);
  const [mappings, setMappings] = useState<ColumnMapping[]>([]);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);

  // Fetch templates
  const { data: templatesData, isLoading: loadingTemplates } = useQuery({
    queryKey: ['import-templates'],
    queryFn: async () => {
      const response = await api.get('/import/templates');
      return response.data.data as ImportTemplate[];
    },
  });

  // Preview mutation
  const previewMutation = useMutation({
    mutationFn: async (formData: FormData) => {
      const response = await api.post('/import/preview', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data.data;
    },
    onSuccess: (data) => {
      setPreviewData(data);
      setMappings(data.suggestedMappings);
      setCurrentStep(3);
    },
  });

  // Execute import mutation
  const importMutation = useMutation({
    mutationFn: async (formData: FormData) => {
      const response = await api.post('/import/execute', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data.data as ImportResult;
    },
    onSuccess: (data) => {
      setImportResult(data);
      setCurrentStep(5);
    },
  });

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
    }
  };

  const handleUpload = () => {
    if (!file || !selectedType) return;
    
    const formData = new FormData();
    formData.append('file', file);
    formData.append('entityType', selectedType);
    previewMutation.mutate(formData);
  };

  const handleMappingChange = (sourceColumn: string, targetField: string) => {
    setMappings(prev => {
      const filtered = prev.filter(m => m.sourceColumn !== sourceColumn);
      if (targetField) {
        return [...filtered, { sourceColumn, targetField }];
      }
      return filtered;
    });
  };

  const handleImport = () => {
    if (!file || !selectedType) return;
    
    const formData = new FormData();
    formData.append('file', file);
    formData.append('entityType', selectedType);
    formData.append('mappings', JSON.stringify(mappings));
    importMutation.mutate(formData);
  };

  const handleDownloadTemplate = async () => {
    if (!selectedType) return;
    
    try {
      const response = await api.get(`/import/templates/${selectedType}/download`, {
        responseType: 'blob',
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${selectedType}_import_template.csv`;
      a.click();
    } catch (error) {
      console.error('Failed to download template', error);
    }
  };

  const resetWizard = () => {
    setCurrentStep(1);
    setSelectedType('');
    setFile(null);
    setPreviewData(null);
    setMappings([]);
    setImportResult(null);
  };

  const selectedTemplate = templatesData?.find(t => t.id === selectedType);

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <Upload className="w-8 h-8 text-primary" />
            Import Data
          </h1>
          <p className="text-muted-foreground">
            Import data from CSV, Excel, or other systems
          </p>
        </div>
        {currentStep > 1 && currentStep < 5 && (
          <Button variant="outline" onClick={resetWizard}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Start Over
          </Button>
        )}
      </div>

      {/* Progress Steps */}
      <div className="flex items-center justify-between">
        {STEPS.map((step, index) => (
          <div key={step.id} className="flex items-center flex-1">
            <div className="flex items-center">
              <div className={cn(
                "w-10 h-10 rounded-full flex items-center justify-center border-2 transition-colors",
                currentStep > step.id
                  ? "bg-primary border-primary text-white"
                  : currentStep === step.id
                  ? "border-primary text-primary"
                  : "border-gray-300 text-gray-400"
              )}>
                {currentStep > step.id ? (
                  <Check className="w-5 h-5" />
                ) : (
                  step.id
                )}
              </div>
              <div className="ml-3 hidden sm:block">
                <p className={cn(
                  "text-sm font-medium",
                  currentStep >= step.id ? "text-foreground" : "text-muted-foreground"
                )}>
                  {step.name}
                </p>
                <p className="text-xs text-muted-foreground">{step.description}</p>
              </div>
            </div>
            {index < STEPS.length - 1 && (
              <div className={cn(
                "flex-1 h-0.5 mx-4",
                currentStep > step.id ? "bg-primary" : "bg-gray-200"
              )} />
            )}
          </div>
        ))}
      </div>

      {/* Step Content */}
      <Card>
        <CardContent className="pt-6">
          {/* Step 1: Select Type */}
          {currentStep === 1 && (
            <div className="space-y-6">
              <h2 className="text-xl font-semibold">What do you want to import?</h2>
              
              {loadingTemplates ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
                </div>
              ) : (
                <div className="grid gap-4 md:grid-cols-3">
                  {templatesData?.map(template => {
                    const Icon = ENTITY_ICONS[template.id] || Package;
                    return (
                      <button
                        key={template.id}
                        onClick={() => setSelectedType(template.id)}
                        className={cn(
                          "p-6 rounded-lg border-2 text-left transition-all hover:shadow-md",
                          selectedType === template.id
                            ? "border-primary bg-primary/5"
                            : "border-gray-200 hover:border-gray-300"
                        )}
                      >
                        <div className="flex items-center gap-3 mb-2">
                          <div className={cn(
                            "p-2 rounded-lg",
                            selectedType === template.id
                              ? "bg-primary/10 text-primary"
                              : "bg-gray-100 text-gray-600"
                          )}>
                            <Icon className="w-6 h-6" />
                          </div>
                          <h3 className="font-semibold">{template.name}</h3>
                        </div>
                        <p className="text-sm text-muted-foreground">{template.description}</p>
                        <p className="text-xs text-muted-foreground mt-2">
                          Required: {template.requiredFields.join(', ')}
                        </p>
                      </button>
                    );
                  })}
                </div>
              )}

              <div className="flex justify-end">
                <Button 
                  onClick={() => setCurrentStep(2)} 
                  disabled={!selectedType}
                >
                  Continue
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          )}

          {/* Step 2: Upload File */}
          {currentStep === 2 && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold">Upload your file</h2>
                <Button variant="outline" size="sm" onClick={handleDownloadTemplate}>
                  <Download className="w-4 h-4 mr-2" />
                  Download Template
                </Button>
              </div>

              <div className="border-2 border-dashed rounded-lg p-12 text-center">
                <input
                  type="file"
                  accept=".csv,.xlsx,.xls"
                  onChange={handleFileChange}
                  className="hidden"
                  id="file-upload"
                />
                <label htmlFor="file-upload" className="cursor-pointer">
                  <FileSpreadsheet className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
                  <p className="text-lg font-medium">
                    {file ? file.name : 'Drop your file here or click to browse'}
                  </p>
                  <p className="text-sm text-muted-foreground mt-2">
                    Supports CSV, XLS, and XLSX files up to 10MB
                  </p>
                  {file && (
                    <p className="text-sm text-green-600 mt-2">
                      ✓ File selected: {(file.size / 1024).toFixed(1)} KB
                    </p>
                  )}
                </label>
              </div>

              {selectedTemplate && (
                <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
                  <h3 className="font-medium text-blue-800 dark:text-blue-200 mb-2">
                    Expected columns for {selectedTemplate.name}:
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {selectedTemplate.requiredFields.map(field => (
                      <span key={field} className="px-2 py-1 bg-red-100 text-red-800 text-xs rounded">
                        {field} (required)
                      </span>
                    ))}
                    {selectedTemplate.optionalFields.map(field => (
                      <span key={field} className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded">
                        {field}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex justify-between">
                <Button variant="outline" onClick={() => setCurrentStep(1)}>
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
                <Button 
                  onClick={handleUpload} 
                  disabled={!file || previewMutation.isPending}
                >
                  {previewMutation.isPending ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : null}
                  Analyze File
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          )}

          {/* Step 3: Map Columns */}
          {currentStep === 3 && previewData && (
            <div className="space-y-6">
              <h2 className="text-xl font-semibold">Map your columns</h2>
              
              <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4">
                <p className="text-green-800 dark:text-green-200">
                  Found {previewData.totalRows} rows with {previewData.headers.length} columns.
                  {previewData.suggestedMappings.length > 0 && (
                    <span> Auto-mapped {previewData.suggestedMappings.length} columns.</span>
                  )}
                </p>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4">Your Column</th>
                      <th className="text-left py-3 px-4">Maps To</th>
                      <th className="text-left py-3 px-4">Sample Data</th>
                    </tr>
                  </thead>
                  <tbody>
                    {previewData.headers.map((header: string) => {
                      const currentMapping = mappings.find(m => m.sourceColumn === header);
                      const sampleValue = previewData.preview[0]?.[header];
                      
                      return (
                        <tr key={header} className="border-b">
                          <td className="py-3 px-4 font-medium">{header}</td>
                          <td className="py-3 px-4">
                            <select
                              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                              value={currentMapping?.targetField || ''}
                              onChange={(e) => handleMappingChange(header, e.target.value)}
                            >
                              <option value="">-- Skip this column --</option>
                              {selectedTemplate?.requiredFields.map(field => (
                                <option key={field} value={field}>{field} (required)</option>
                              ))}
                              {selectedTemplate?.optionalFields.map(field => (
                                <option key={field} value={field}>{field}</option>
                              ))}
                            </select>
                          </td>
                          <td className="py-3 px-4 text-sm text-muted-foreground">
                            {sampleValue !== undefined ? String(sampleValue).substring(0, 50) : '-'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="flex justify-between">
                <Button variant="outline" onClick={() => setCurrentStep(2)}>
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
                <Button onClick={() => setCurrentStep(4)}>
                  Review Import
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          )}

          {/* Step 4: Review */}
          {currentStep === 4 && previewData && (
            <div className="space-y-6">
              <h2 className="text-xl font-semibold">Review and Import</h2>
              
              <div className="grid gap-4 md:grid-cols-3">
                <Card>
                  <CardContent className="pt-6 text-center">
                    <FileSpreadsheet className="w-12 h-12 mx-auto text-muted-foreground mb-2" />
                    <p className="text-2xl font-bold">{previewData.totalRows}</p>
                    <p className="text-sm text-muted-foreground">Total Rows</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6 text-center">
                    <Check className="w-12 h-12 mx-auto text-green-600 mb-2" />
                    <p className="text-2xl font-bold">{mappings.length}</p>
                    <p className="text-sm text-muted-foreground">Mapped Columns</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6 text-center">
                    <AlertCircle className="w-12 h-12 mx-auto text-amber-600 mb-2" />
                    <p className="text-2xl font-bold">{previewData.totalErrors || 0}</p>
                    <p className="text-sm text-muted-foreground">Validation Warnings</p>
                  </CardContent>
                </Card>
              </div>

              {previewData.validationErrors?.length > 0 && (
                <div className="bg-amber-50 dark:bg-amber-900/20 rounded-lg p-4">
                  <h3 className="font-medium text-amber-800 dark:text-amber-200 mb-2 flex items-center gap-2">
                    <AlertCircle className="w-5 h-5" />
                    Validation Warnings (first 10)
                  </h3>
                  <ul className="text-sm text-amber-700 dark:text-amber-300 space-y-1">
                    {previewData.validationErrors.slice(0, 10).map((error: ValidationError, i: number) => (
                      <li key={i}>Row {error.row}: {error.message}</li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="bg-muted rounded-lg p-4">
                <h3 className="font-medium mb-2">Preview (first 5 rows)</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        {mappings.map(m => (
                          <th key={m.targetField} className="text-left py-2 px-3">{m.targetField}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {previewData.preview.slice(0, 5).map((row: any, i: number) => (
                        <tr key={i} className="border-b">
                          {mappings.map(m => (
                            <td key={m.targetField} className="py-2 px-3">
                              {row[m.sourceColumn] !== undefined ? String(row[m.sourceColumn]).substring(0, 30) : '-'}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="flex justify-between">
                <Button variant="outline" onClick={() => setCurrentStep(3)}>
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
                <Button 
                  onClick={handleImport}
                  disabled={importMutation.isPending}
                  className="bg-green-600 hover:bg-green-700"
                >
                  {importMutation.isPending ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Upload className="w-4 h-4 mr-2" />
                  )}
                  Import {previewData.totalRows} Records
                </Button>
              </div>
            </div>
          )}

          {/* Step 5: Results */}
          {currentStep === 5 && importResult && (
            <div className="space-y-6">
              <div className="text-center py-8">
                {importResult.success ? (
                  <CheckCircle className="w-20 h-20 mx-auto text-green-600 mb-4" />
                ) : (
                  <XCircle className="w-20 h-20 mx-auto text-amber-600 mb-4" />
                )}
                <h2 className="text-2xl font-bold mb-2">
                  {importResult.success ? 'Import Complete!' : 'Import Completed with Issues'}
                </h2>
                <p className="text-muted-foreground">
                  Successfully imported {importResult.imported} of {importResult.totalRows} records
                </p>
              </div>

              <div className="grid gap-4 md:grid-cols-3">
                <Card>
                  <CardContent className="pt-6 text-center">
                    <CheckCircle className="w-12 h-12 mx-auto text-green-600 mb-2" />
                    <p className="text-3xl font-bold text-green-600">{importResult.imported}</p>
                    <p className="text-sm text-muted-foreground">Imported</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6 text-center">
                    <AlertCircle className="w-12 h-12 mx-auto text-amber-600 mb-2" />
                    <p className="text-3xl font-bold text-amber-600">{importResult.skipped}</p>
                    <p className="text-sm text-muted-foreground">Skipped</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6 text-center">
                    <XCircle className="w-12 h-12 mx-auto text-red-600 mb-2" />
                    <p className="text-3xl font-bold text-red-600">{importResult.errors.length}</p>
                    <p className="text-sm text-muted-foreground">Errors</p>
                  </CardContent>
                </Card>
              </div>

              {importResult.errors.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Error Details</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="max-h-64 overflow-y-auto space-y-2">
                      {importResult.errors.map((error, i) => (
                        <div key={i} className="text-sm p-2 bg-red-50 dark:bg-red-900/20 rounded">
                          <span className="font-medium">Row {error.row}:</span> {error.message}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              <div className="flex justify-center gap-4">
                <Button variant="outline" onClick={resetWizard}>
                  Import More Data
                </Button>
                <Button onClick={() => window.location.href = `/${selectedType}`}>
                  View {selectedTemplate?.name}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
